/**
 * Users and Students API endpoints
 */

import { fetchApi, buildQueryString, withPagination } from '../core';
import type { QueryParams, UpdateUserData, NoteData, PlacementTestSubmission } from '../types';
import type { LegacyUser, User, PaginatedResponse } from '@/types';
import { objectIdPath } from '../path';

type UserListResponse = PaginatedResponse<LegacyUser[]> | LegacyUser[];
type StudentListResponse = PaginatedResponse<User[]> | User[];

// Users API
export const users = {
  getAll(params?: QueryParams, componentName: string = 'Unknown') {
    const paginatedParams = withPagination(params, true); // Skip pagination if fetching by IDs
    return fetchApi<UserListResponse>(`/users${buildQueryString(paginatedParams)}`, {}, componentName);
  },

  getById(id: string, componentName: string = 'Unknown') {
    return fetchApi<LegacyUser>(`/users/${objectIdPath(id, 'user')}`, {}, componentName);
  },

  update(id: string, data: UpdateUserData, componentName: string = 'Unknown') {
    return fetchApi<LegacyUser>(`/users/${objectIdPath(id, 'user')}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }, componentName);
  },

  assignCourse(userId: string, courseId: string, componentName: string = 'Unknown') {
    const safeUserId = objectIdPath(userId, 'user');
    const safeCourseId = objectIdPath(courseId, 'course');
    // Enrollment has roster, ledger, and audit effects. Keep this helper
    // deliberately singular so callers cannot mistake sequential partial
    // commits for an atomic bulk assignment.
    return fetchApi(`/courses/${safeCourseId}/students`, {
      method: 'POST',
      body: JSON.stringify({ student_id: safeUserId })
    }, componentName);
  },

  updateNote(userId: string, oldNote: NoteData, newNote: NoteData, componentName: string = 'Unknown') {
    return fetchApi(`/users/${objectIdPath(userId, 'user')}/notes`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ old_note: oldNote, new_note: newNote })
    }, componentName);
  },

  deleteNote(userId: string, note: NoteData, componentName: string = 'Unknown') {
    return fetchApi(`/users/${objectIdPath(userId, 'user')}/notes`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note })
    }, componentName);
  },

  getPlacementTests(userId: string, componentName: string = 'Unknown') {
    return fetchApi<PlacementTestSubmission[]>(`/users/${objectIdPath(userId, 'user')}/placement-tests`, {}, componentName);
  }
};

// Students API (separate from Users - for instructors and admins)
export const students = {
  getAll(params?: QueryParams, componentName: string = 'Unknown') {
    const paginatedParams = {
      ...withPagination(params),
      role: 'Student'
    };
    return fetchApi<StudentListResponse>(`/users${buildQueryString(paginatedParams)}`, {}, componentName);
  },

  getMy(params?: QueryParams, componentName: string = 'Unknown') {
    const paginatedParams = withPagination(params);
    return fetchApi<StudentListResponse>(`/my/students${buildQueryString(paginatedParams)}`, {}, componentName);
  },

  getByCourse(courseId: string, params?: QueryParams, componentName: string = 'Unknown') {
    const paginatedParams = withPagination(params);
    return fetchApi<StudentListResponse>(`/courses/${objectIdPath(courseId, 'course')}/students${buildQueryString(paginatedParams)}`, {}, componentName);
  }
};
